// KPI chart shaping shared by the realtime Vertical page and the incidents "View in Monitor" modal.

export const toNumberArray = (arr) =>
  Array.isArray(arr)
    ? arr.map((val) => (val === null || val === undefined || val === "" ? null : Number(val)))
    : [];

/**
 * Enrich data with date fields if not already present.
 * Computes: last_processed_time, first_processed_time, start_date, end_date from time array
 */
export const enrichDateFields = (data, timeArray) => {
  const enriched = { ...data };

  if (!timeArray || timeArray.length === 0) {
    // No time data, use current date as fallback
    const now = new Date().toISOString().split('T')[0];
    enriched.last_processed_time = enriched.last_processed_time || new Date().toLocaleString('en-GB', { timeZone: 'UTC' });
    enriched.first_processed_time = enriched.first_processed_time || new Date().toLocaleString('en-GB', { timeZone: 'UTC' });
    enriched.start_date = enriched.start_date || now;
    enriched.end_date = enriched.end_date || now;
    return enriched;
  }

  // Extract date from various time formats
  const extractDateFromTimeString = (timeStr) => {
    if (!timeStr) return null;
    // Handle range format: "2026-05-26 - 2026-06-01"
    if (timeStr.includes(' - ')) {
      const parts = timeStr.split(' - ');
      return parts[1]?.trim() || parts[0]?.trim();
    }
    // Handle single date format: "2026-06-01"
    if (timeStr.match(/^\d{4}-\d{2}-\d{2}/)) {
      return timeStr.split(' ')[0];
    }
    return null;
  };

  const firstDate = extractDateFromTimeString(timeArray[0]);
  const lastDate = extractDateFromTimeString(timeArray[timeArray.length - 1]);

  if (!enriched.last_processed_time && lastDate) {
    enriched.last_processed_time = `${lastDate} 00:00:00`;
  } else if (!enriched.last_processed_time) {
    enriched.last_processed_time = new Date().toLocaleString('en-GB', { timeZone: 'UTC' });
  }

  if (!enriched.first_processed_time && firstDate) {
    enriched.first_processed_time = `${firstDate} 00:00:00`;
  } else if (!enriched.first_processed_time) {
    enriched.first_processed_time = new Date().toLocaleString('en-GB', { timeZone: 'UTC' });
  }

  if (!enriched.start_date && firstDate) {
    enriched.start_date = firstDate;
  } else if (!enriched.start_date) {
    enriched.start_date = new Date().toISOString().split('T')[0];
  }

  if (!enriched.end_date && lastDate) {
    enriched.end_date = lastDate;
  } else if (!enriched.end_date) {
    enriched.end_date = new Date().toISOString().split('T')[0];
  }

  return enriched;
};

export const normalizeKpiSeries = (raw, fallbackName, kpiKey) => {
  const time = Array.isArray(raw?.time) ? raw.time : [];
  const value = toNumberArray(raw?.value);
  const drift = toNumberArray(raw?.drift);
  const metricThreshold = Array.isArray(raw?.metric_threshold)
    ? raw.metric_threshold
    : Array.isArray(raw?.kpi_threshold)
      ? raw.kpi_threshold
      : new Array(time.length).fill(null);
  const metricThresholdErrorStatus = Array.isArray(raw?.metric_threshold_error_status)
    ? raw.metric_threshold_error_status
    : Array.isArray(raw?.kpi_threshold_error_status)
      ? raw.kpi_threshold_error_status
      : Array.isArray(raw?.metrics_threshold_error_status)
        ? raw.metrics_threshold_error_status
        : new Array(time.length).fill(false);

  // Calculate metric_threshold_error_diff_value if not present
  const metricThresholdErrorDiffValue = Array.isArray(raw?.metric_threshold_error_diff_value)
    ? raw.metric_threshold_error_diff_value
    : Array.isArray(raw?.kpi_threshold_error_diff_value)
      ? raw.kpi_threshold_error_diff_value
      : time.map((_, index) => {
          const val = value[index];
          const threshold = metricThreshold[index];
          if (val !== null && threshold !== null && val > threshold) {
            return val - threshold;
          }
          return 0;
        });

  // Generate drift_patterns array based on drift_pattern or individual drift values
  const driftPattern = raw?.drift_pattern || "NO_DRIFT";
  const driftPatterns = Array.isArray(raw?.drift_patterns)
    ? raw.drift_patterns
    : new Array(time.length).fill(driftPattern);

  const enrichedData = enrichDateFields(raw, time);

  return {
    ...enrichedData,
    id: Number(kpiKey),
    name: raw?.name || fallbackName || "KPI",
    time,
    value,
    drift: drift.length > 0 ? drift : value, // Use drift if available, otherwise use value
    data_type: Array.isArray(raw?.data_type) ? raw.data_type : new Array(time.length).fill(null),
    error_rows: Array.isArray(raw?.error_rows) ? raw.error_rows : new Array(time.length).fill(null),
    boxplot_data: Array.isArray(raw?.boxplot_data) ? raw.boxplot_data : new Array(time.length).fill(null),
    contribution: Array.isArray(raw?.contribution) ? raw.contribution : new Array(time.length).fill(null),
    version_name: Array.isArray(raw?.version_name) ? raw.version_name : new Array(time.length).fill(null),
    drift_pattern: driftPattern,
    drift_patterns: driftPatterns,
    threshold_value: raw?.threshold_value ?? raw?.kpi_threshold_value ?? null,
    kpi_threshold_error_status: Array.isArray(raw?.kpi_threshold_error_status)
      ? raw.kpi_threshold_error_status
      : metricThresholdErrorStatus,
    kpi_threshold_error_diff_value: Array.isArray(raw?.kpi_threshold_error_diff_value)
      ? raw.kpi_threshold_error_diff_value
      : metricThresholdErrorDiffValue,
    metric_threshold: metricThreshold,
    metric_threshold_error_status: metricThresholdErrorStatus,
    metric_threshold_error_diff_value: metricThresholdErrorDiffValue,
    normalization_value: Array.isArray(raw?.normalization_value) ? raw.normalization_value : new Array(time.length).fill(null),
    is_all_string_column: Array.isArray(raw?.is_all_string_column) ? raw.is_all_string_column : new Array(time.length).fill(null)
  };
};
